/**
 * Firebase Error Handler
 * 
 * Utility functions for handling and resolving common Firebase errors
 */

import { forceNetworkReset } from '../firebase.js';
import { resetAllFirebaseConnections } from '../forceReset.js';

/**
 * Error handler for Firebase operations
 * Detects common Firebase errors and provides user-friendly messages
 */

// Check if an error is related to connectivity
export const isConnectivityError = (error) => {
  if (!error) return false;
  
  // Common FirebaseError codes related to connectivity
  const connectivityCodes = [
    'auth/network-request-failed',
    'auth/timeout',
    'unavailable',
    'deadline-exceeded',
    'operation-not-allowed',
    'internal'
  ];
  
  // Check if error code or message indicates connectivity issues
  const isConnectivityCode = error.code && connectivityCodes.some(code => error.code.includes(code));
  const isConnectivityMessage = error.message && (
    error.message.includes('network') ||
    error.message.includes('timeout') ||
    error.message.includes('unavailable') ||
    error.message.includes('connection') ||
    error.message.includes('connect')
  );
  
  return isConnectivityCode || isConnectivityMessage;
};

// Process Firebase error object into user-friendly message
export const getFirebaseErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';
  
  // Check for connectivity issues first
  if (isConnectivityError(error)) {
    return 'Connection issue. Please check your internet connection and try again.';
  }
  
  // Common auth error codes and their user-friendly messages
  const errorMessages = {
    'auth/invalid-email': 'The email address is not valid.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/email-already-in-use': 'This email is already in use by another account.',
    'auth/weak-password': 'The password is too weak.',
    'auth/requires-recent-login': 'Please log in again to complete this action.',
    'auth/popup-closed-by-user': 'Login popup was closed before completion.',
    'auth/cancelled-popup-request': 'The login request was cancelled.',
    'auth/popup-blocked': 'The login popup was blocked by your browser.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
    'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in credentials.',
    'auth/invalid-credential': 'The authentication credential is invalid.',
    'auth/operation-not-allowed': 'This sign-in method is not allowed.',
    'auth/invalid-verification-code': 'The verification code is invalid.',
    'auth/invalid-verification-id': 'The verification ID is invalid.',
    'auth/missing-verification-code': 'The verification code is missing.',
    'auth/missing-verification-id': 'The verification ID is missing.',
    'permission-denied': 'You do not have permission to access this resource.',
    'resource-exhausted': 'You have exceeded your quota or rate limit.',
    'not-found': 'The requested resource was not found.'
  };
  
  // Get user-friendly message or use Firebase error message
  return errorMessages[error.code] || 
         error.message || 
         'An error occurred. Please try again.';
};

// Attempt to resolve connectivity errors
export const resolveConnectivityError = async () => {
  try {
    // Try to reset Firebase network connections
    const resetResult = await forceNetworkReset();
    
    if (!resetResult.success) {
      // If the simple reset failed, try a more drastic reset
      await resetAllFirebaseConnections();
    }
    
    return true;
  } catch (error) {
    console.error('Error resolving connectivity issue:', error);
    throw new Error('Failed to resolve connectivity issue. Please check your internet connection and try again.');
  }
};

// Parse Firebase error object to get the code and message
export const parseFirebaseError = (error) => {
  // If it's a Firebase Auth or Firestore error
  if (error.code) {
    return {
      code: error.code,
      message: getFirebaseErrorMessage(error),
      originalError: error
    };
  }
  
  // For generic errors, try to determine if it's connectivity related
  if (error.message && (
    error.message.toLowerCase().includes('network') ||
    error.message.toLowerCase().includes('timeout') ||
    error.message.toLowerCase().includes('offline')
  )) {
    return {
      code: 'network-error',
      message: getFirebaseErrorMessage({ code: 'network-error' }),
      originalError: error
    };
  }
  
  // Default error handling
  return {
    code: 'default',
    message: getFirebaseErrorMessage({ code: 'default' }),
    originalError: error
  };
}; 